import { FilterField, labelId } from "kontrolEdge/helpers/advancedFilters";
import { keListTagFilterHaystack } from "kontrolEdge/components/Shared/mapKeListResourceTags";
import {
  physicalConnectionIds,
  physicalConnectionType,
} from "kontrolEdge/helpers/physicalConnections";
import { portLocationSearchText } from "kontrolEdge/helpers/portLocation";
import { Port } from "./PortData";

// Capacity parser: return Mbps
function parseCapacityInput(raw: string): number | null {
  const s = raw.trim().toLowerCase().replace(/\s+/g, "").replace(/,/g, "");
  // "1gbps", "1g", "1gb" → Gbps
  if (/(gbps?|g)b?$/.test(s)) {
    const n = Number(s.replace(/(gbps?|g)b?$/, ""));
    return Number.isFinite(n) ? n * 1000 : null;
  }
  // "750mbps", "750m", "750mb" → Mbps
  if (/(mbps?|m)b?$/.test(s)) {
    const n = Number(s.replace(/(mbps?|m)b?$/, ""));
    return Number.isFinite(n) ? n : null;
  }
  // plain number → assume Mbps
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

// Date parser: accept DD/MM/YYYY, YYYY-MM-DD, MM/DD/YYYY
function parseDateInput(raw: string): Date | null {
    const s = raw.trim();
    // allow 1–2 digits for day/month
    const m1 = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/); // D/M/YYYY or DD/MM/YYYY
    if (m1) {
        const [, dd, mm, yyyy] = m1;
        const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd)); // local midnight
        return isNaN(d.getTime()) ? null : d;
    }
    const m2 = s.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/); // YYYY-M-D or YYYY-MM-DD
    if (m2) {
        const [, yyyy, mm, dd] = m2;
        const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
        return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s); // ISO fallback
    return isNaN(d.getTime()) ? null : d;
    }

export function makeBasePortFieldSpecs(): FilterField<Port>[] {
  return [
    {
      label: "Port ID",
      key: "portid",
      accessor: (r) => r.portId,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Name",
      key: "name",
      accessor: (r) => r.name,
      type: "string",
      inFreeText: true,
    },
    // Use raw date for comparisons; users still type human text
    {
      label: "Renew Date",
      key: "renewdate",
      accessor: (r) => r.renewDateRaw,
      type: "date",
      inFreeText: true,
      parseRhs: parseDateInput,
    },
    // If you ever want strict numeric ops (>=, <=), keep capacity numeric here
    {
      label: "Capacity",
      key: "capacity",
      accessor: (r) => r.capacity,
      type: "number",
      inFreeText: true,
      parseRhs: parseCapacityInput,
    },
    // Filter on the **label** users expect.
    {
      label: "Port Status",
      key: "portstatus",
      accessor: (r) => {
        const s = r.state?.actualState;
        if (s === "up") return "Active";
        if (s === "down") return "Down";
        return "Unknown";
      },
      type: "string",
      inFreeText: true,
    },
    {
      label: "Tag",
      key: "tag",
      accessor: (r) => keListTagFilterHaystack(r),
      type: "string",
      inFreeText: true,
    },
    {
      label: "Physical Connect Type",
      key: "physicalconnecttype",
      accessor: (r) => physicalConnectionType(r.physicalConnection),
      type: "string",
      inFreeText: true,
    },
    {
      // Searchable beyond what the column shows, on purpose:
      //  - `crossConnectId` stays in the haystack even when carrier access wins
      //    and hides it, so searching a cross connect id never stops finding its
      //    port.
      //  - the raw carrier code is folded in, so "ConsoleConnect" and "CC" find
      //    the same ports.
      //  - the Backhaul (84-) a leg backs, so its id finds its ports.
      label: "Physical Connect ID",
      key: "physicalconnectid",
      accessor: (r) =>
        [
          physicalConnectionIds(r.physicalConnection),
          r.crossConnectId ?? "",
          ...r.carrierAccess.map((c) => c.carrierCode),
          ...r.carrierAccess.map((c) => c.carrierAccess?.serviceId ?? ""),
        ]
          .filter(Boolean)
          .join(" "),
      type: "string",
      inFreeText: true,
    },
    {
      label: "Term",
      key: "term",
      accessor: (r) => r.term,
      type: "string",
      inFreeText: true,
    },
    {
      // A port delivered by an Extended PoP is found by that site (CTX-802).
      label: "POP",
      key: "pop",
      accessor: (r) => portLocationSearchText(r),
      type: "string",
      inFreeText: true,
    },
  ];
}

export function makePortFields(
  isViewingAllCustomers: boolean
): FilterField<Port>[] {
  const base = makeBasePortFieldSpecs();
  if (isViewingAllCustomers) {
    base.splice(1, 0, {
      label: "Customer",
      key: "customer",
      accessor: (r) => [
        r.customer?.name ?? "",
        r.customer?.chargebeeCustomerId ?? ""
      ].filter(Boolean).join(" "),
      type: "string",
      inFreeText: true,
    });
  }
  // Ensure keys
  for (const f of base) f.key ||= labelId(f.label);
  return base;
}
